import { useGetShopDisplayUnit } from "@/lib/queries/useGetShopSettings";
import {
  AreaUnit,
  convertUnit,
  inferUnitCategory,
  LengthUnit,
} from "@/lib/units";
import { useState } from "react";
import { useParams } from "react-router";
import useOperationContext from "./useOperationContext";
import { UnitType } from "./unit";
import { getIn } from "@/lib/utils";

function convert(
  value: string | null,
  fromUnit: LengthUnit | AreaUnit | null,
  toUnit: LengthUnit | AreaUnit | null,
): string | null {
  if (!value || !fromUnit || !toUnit) return null;
  if (fromUnit === toUnit) return value;

  const category = inferUnitCategory(fromUnit);
  const isConvertible = category === inferUnitCategory(toUnit);

  if (!isConvertible) {
    // don't break the app if non-convertible units weren't caught in the hook
    console.error(
      `Can't convert from ${fromUnit} to ${toUnit}. Please fix the useDisplayLengthUnit hook.`,
    );
    return value;
  }

  const valueAsNumber = Number(value);
  const convertedValue = Number.isNaN(valueAsNumber)
    ? value
    : convertUnit(valueAsNumber, fromUnit, toUnit);

  return convertedValue?.toString() ?? null;
}

const noConversion = {
  displayLengthUnit: null,
  unitKey: null,
  setOverrideDisplayUnit: null,
  toDisplayUnit: null,
  fromDisplayUnit: null,
};

export default function useDisplayLengthUnit({
  keyPath,
  unit,
  nunit,
  dunit,
  skipConversion = false,
}: {
  keyPath: readonly PropertyKey[];
  skipConversion?: boolean;
  unit?: UnitType;
  nunit?: UnitType;
  dunit?: UnitType;
}) {
  const { teamSlug } = useParams();
  const {
    operationData: { augmented },
  } = useOperationContext();

  const shopDisplayUnit = useGetShopDisplayUnit(teamSlug);
  const [overrideDisplayUnit, setOverrideDisplayUnit] = useState<LengthUnit>();

  if (skipConversion) return noConversion;

  const unitKey =
    unit === "area" || unit === "length"
      ? "unit"
      : nunit === "area" || nunit === "length"
        ? "nunit"
        : dunit === "area" || dunit === "length"
          ? "dunit"
          : null;
  if (!unitKey) return noConversion;

  const augmentedUnit = getIn(augmented, [...keyPath, unitKey], null) as
    | LengthUnit
    | AreaUnit
    | null;
  if (!augmentedUnit) return noConversion;

  let displayUnit: LengthUnit | AreaUnit =
    overrideDisplayUnit ?? shopDisplayUnit;
  if ((unit === "area" || dunit === "area") && !displayUnit.endsWith("2")) {
    displayUnit = `${displayUnit}2` as AreaUnit;
  }

  return {
    displayLengthUnit: displayUnit ?? augmentedUnit,
    unitKey,
    setOverrideDisplayUnit,
    toDisplayUnit: (value: string | null) => {
      return convert(value, augmentedUnit, displayUnit);
    },
    fromDisplayUnit: (value: string | null) => {
      return convert(value, displayUnit, augmentedUnit);
    },
  };
}
